import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  TableCellsIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { Button, Input, Select, Card, Modal, ModalFooter, LoadingSpinner } from '@/components/common';
import { AdminCrudFormModal } from './AdminCrudFormModal';
import { BulkActionBar } from './BulkActionBar';
import { ImportExportControls } from './ImportExportControls';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import api, { getErrorMessage } from '@/services/api';
import { cn } from '@/utils';

// --- Types ---

interface ColumnConfig<T> {
  key: string;
  label: string;
  /**
   * Opt IN to a sortable header. Default false, deliberately.
   *
   * This was opt-OUT, so every column rendered a clickable header that set
   * `ordering=<key>`. DRF's `OrderingFilter` silently ignores a value outside
   * the viewset's `ordering_fields`, and the arrow indicator is drawn from
   * client state -- so most columns flipped an arrow and changed nothing, and
   * a column whose key is a serializer alias (`product_name`, `group_name`,
   * `addon_type_display`) could never be ordered at all. A control that
   * appears to work and does nothing is worse than no control.
   *
   * Set this true only for a key that really is in the viewset's
   * `ordering_fields`.
   */
  sortable?: boolean;
  render?: (value: unknown, row: T) => React.ReactNode;
  /**
   * Column width, read only when the page config sets `tableLayout: 'fixed'`.
   * Any valid CSS width ('120px', '10rem', '20%', ...), applied via a
   * `<colgroup>` -- see `Table.tsx`'s identical field for why a colgroup and
   * not a Tailwind class (a runtime-templated `w-[${width}]` is invisible to
   * Tailwind's JIT scanner).
   */
  width?: string;
}

interface FormFieldConfig {
  name: string;
  label: string;
  type: 'text' | 'select' | 'number' | 'date' | 'boolean';
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  apiEndpoint?: string;
  searchable?: boolean;
}

interface FilterConfig {
  key: string;
  label: string;
  options: Array<{ value: string; label: string }>;
}

export interface AdminCrudPageConfig<T> {
  title: string;
  apiEndpoint: string;
  queryKey: string;
  columns: ColumnConfig<T>[];
  formFields: FormFieldConfig[];
  searchPlaceholder?: string;
  filters?: FilterConfig[];
  hasActivate?: boolean;
  /**
   * Render the bulk CSV/Excel Import + Export controls.
   *
   * OPT-IN, and deliberately so. `ImportExportControls` calls
   * `<apiEndpoint>export/`, `<apiEndpoint>import-template/` and
   * `<apiEndpoint>import/`, which exist only on viewsets that mix in
   * `core.import_export.ImportExportMixin`. Of the eight screens this
   * component backs, only `/products/types/` and `/products/subgroups/` do.
   * On the other six the DRF router has no such action, so its DETAIL
   * pattern matches instead and `GET /ledger/fiscal-years/export/` becomes a
   * lookup for a fiscal year with the primary key "export" -- the 404 users
   * reported (#49), one page-level toast reading "Request failed with status
   * code 404" with nothing to do about it.
   *
   * Rendering a control that cannot work is worse than not offering it, so
   * the default is off and a config turns it on only once its endpoint
   * really supports it.
   */
  hasImportExport?: boolean;
  /**
   * Replace the generic create modal with a bespoke one, for entities whose
   * creation is not "POST the same fields the edit form shows".
   *
   * Insurer Employees (#203) is the case this exists for: creating one means
   * provisioning a User account -- the standard Add User form's fields, sent
   * to `/insurers/insurers/{id}/create_user/` -- while EDITING one is an
   * ordinary PATCH of the mapping row. Only the create half is overridden;
   * list, search, sort, edit, delete and bulk actions are untouched.
   *
   * The callback owns rendering its own modal. `onClose` closes without
   * refetching; `onCreated` invalidates this page's query and then closes,
   * so a bespoke form never has to know this page's query key.
   */
  renderCreateForm?: (args: { onClose: () => void; onCreated: () => void }) => React.ReactNode;
  /**
   * Extra warning shown in the delete confirmation, for entities where
   * deleting does more than remove the row.
   *
   * Insurer Employees is the case this exists for: removing one now also
   * revokes the person's login. The generic "this action cannot be undone" says
   * nothing about that, and an admin who is not told is entitled to be
   * surprised. Optional and additive -- every other page reads exactly as
   * before.
   */
  deleteWarning?: React.ReactNode;
  pageSize?: number;
  entityName?: string;
  /**
   * G2 ("columns shift when filters are applied"): this component hand-rolls
   * its own `<table>` rather than the shared `Table.tsx` primitive (sorting,
   * the checkbox column and the row-actions column are all bespoke here), so
   * it needed its own copy of the fixed-layout option that primitive grew --
   * see `Table.tsx`'s identical prop for the full reasoning. 'auto' (the
   * default) is `table-auto`, byte-for-byte what every screen on this
   * component had before; 'fixed' switches to `table-fixed` and reads each
   * `ColumnConfig.width` via a `<colgroup>`. Opt-in, because `table-fixed`
   * with no widths collapses every column to an equal share of the table,
   * which looks worse than `table-auto` for a config that has not set
   * `width` on its columns yet.
   */
  tableLayout?: 'auto' | 'fixed';
  /**
   * Truncate long cell content with an ellipsis (plus a `title` tooltip for
   * plain text/number content). Only meaningful with `tableLayout: 'fixed'`.
   * Defaults to `true` whenever `tableLayout` is `'fixed'`, matching
   * `Table.tsx`.
   */
  truncateCells?: boolean;
  /**
   * Opt-in "View" row action, opening a read-only detail popup.
   *
   * This component had no View action at all before item 6/8 of the
   * 2026-09-18 batch (Insurance Types, Product Subgroups) -- only Edit and
   * Delete. Rather than have each page hand-roll its own "view" modal (and
   * its own way of triggering it), the icon button and the Modal chrome live
   * here; the caller supplies only the content and, optionally, the title.
   * A page that does not pass this keeps exactly its previous three-button
   * row (Edit, Delete) -- this is purely additive.
   */
  renderView?: (item: T) => React.ReactNode;
  /** Title for the View modal. Defaults to `${entity} details`. */
  viewTitle?: (item: T) => string;
}

interface PaginatedResponse<T> {
  count: number;
  results: T[];
  next: string | null;
  previous: string | null;
}

// --- API helpers ---

function buildListUrl(
  endpoint: string,
  params: {
    page: number;
    page_size: number;
    search?: string;
    ordering?: string;
    filters?: Record<string, string>;
  }
) {
  const url = new URL(endpoint, window.location.origin);
  url.searchParams.set('page', String(params.page));
  url.searchParams.set('page_size', String(params.page_size));
  if (params.search) url.searchParams.set('search', params.search);
  if (params.ordering) url.searchParams.set('ordering', params.ordering);
  if (params.filters) {
    Object.entries(params.filters).forEach(([k, v]) => {
      if (v) url.searchParams.set(k, v);
    });
  }
  return url.pathname + url.search;
}

// --- Component ---

export function AdminCrudPage<T extends { id: string }>({
  config,
}: {
  config: AdminCrudPageConfig<T>;
}) {
  const {
    title,
    apiEndpoint,
    queryKey,
    columns,
    formFields,
    searchPlaceholder = 'Search...',
    filters = [],
    hasActivate = false,
    hasImportExport = false,
    renderCreateForm,
    pageSize = 20,
    entityName,
    renderView,
    viewTitle,
    tableLayout = 'auto',
    truncateCells = tableLayout === 'fixed',
  } = config;
  const isFixedLayout = tableLayout === 'fixed';

  const entity = entityName || title.replace(/s$/, '');
  const queryClient = useQueryClient();

  // State
  const [page, setPage] = useState(1);
  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `search` drives the Input synchronously, `debouncedSearch` (300ms) is
  // what actually feeds the query below.
  const { term: search, debouncedTerm: debouncedSearch, setTerm: setSearch } = useDebouncedSearch();
  const [ordering, setOrdering] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<T | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<T | null>(null);
  const [viewTarget, setViewTarget] = useState<T | null>(null);

  // Fetch list
  const listUrl = buildListUrl(apiEndpoint, {
    page,
    page_size: pageSize,
    search: debouncedSearch || undefined,
    ordering: ordering || undefined,
    filters: filterValues,
  });

  const { data, isLoading } = useQuery<PaginatedResponse<T>>({
    queryKey: [queryKey, page, debouncedSearch, ordering, filterValues],
    queryFn: async () => {
      const response = await api.get(listUrl);
      return response.data;
    },
  });

  const items = useMemo(() => data?.results || [], [data?.results]);
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Mutations
  const createMutation = useMutation({
    mutationFn: async (formData: Record<string, unknown>) => {
      const response = await api.post(apiEndpoint, formData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data: formData }: { id: string; data: Record<string, unknown> }) => {
      const response = await api.patch(`${apiEndpoint}${id}/`, formData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`${apiEndpoint}${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
  });

  /**
   * Run one request per id and report per id, never all-or-nothing.
   *
   * `Promise.all` rejects on the FIRST failure while the other requests still
   * complete on the server. That left the screen in a lie: `onSuccess` never
   * ran, so the table was not refetched and the selection was not cleared,
   * while some rows really had been deleted or deactivated. The person saw an
   * error and a table still showing rows that were already gone.
   *
   * This became newly likely when the master-data viewsets gained the guarded
   * delete: a referenced row now answers 409, so a mixed selection -- some
   * free, some in use -- is the ORDINARY case rather than an edge one.
   *
   * `allSettled` lets every request finish, then the result says exactly what
   * happened. The rule matches `BulkActionBar`: refetch whenever anything
   * changed, clear the selection only when everything succeeded, and keep the
   * failures selected so they can be seen and retried.
   */
  const runPerId = async (
    ids: string[],
    request: (id: string) => Promise<unknown>
  ): Promise<{ succeeded: string[]; failed: { id: string; message: string }[] }> => {
    const outcomes = await Promise.allSettled(ids.map((id) => request(id)));
    const succeeded: string[] = [];
    const failed: { id: string; message: string }[] = [];
    outcomes.forEach((outcome, index) => {
      if (outcome.status === 'fulfilled') succeeded.push(ids[index]);
      else failed.push({ id: ids[index], message: getErrorMessage(outcome.reason) });
    });
    return { succeeded, failed };
  };

  const settleBulk = (result: { succeeded: string[]; failed: { id: string; message: string }[] }) => {
    if (result.succeeded.length) {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    }
    if (!result.failed.length) {
      setSelectedIds(new Set());
      return;
    }
    // Keep the refused rows selected, and say what stopped them -- the server's
    // own words, which for a guarded delete name what is still using the row.
    setSelectedIds(new Set(result.failed.map((f) => f.id)));
    const [first] = result.failed;
    const extra = result.failed.length - 1;
    toast.error(
      result.succeeded.length
        ? `${result.succeeded.length} done. ${result.failed.length} could not be: ${first.message}${extra > 0 ? ` (and ${extra} more)` : ''}`
        : `${first.message}${extra > 0 ? ` (and ${extra} more)` : ''}`
    );
  };

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => runPerId(ids, (id) => api.delete(`${apiEndpoint}${id}/`)),
    onSuccess: settleBulk,
  });

  const activateMutation = useMutation({
    mutationFn: ({ ids, active }: { ids: string[]; active: boolean }) =>
      runPerId(ids, (id) => api.patch(`${apiEndpoint}${id}/`, { is_active: active })),
    onSuccess: settleBulk,
  });

  // Handlers
  const handleOpenCreate = () => {
    setEditingItem(null);
    setIsFormOpen(true);
  };

  const handleOpenEdit = (item: T) => {
    setEditingItem(item);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingItem(null);
  };

  // A config-supplied create form takes over ONLY when opening a create (no
  // editingItem). Edits keep the generic modal, so overriding create never
  // costs a page its edit form.
  const isCustomCreateOpen = Boolean(renderCreateForm) && isFormOpen && editingItem === null;

  const handleCustomCreated = () => {
    queryClient.invalidateQueries({ queryKey: [queryKey] });
    handleCloseForm();
  };

  const handleFormSubmit = async (formData: Record<string, unknown>) => {
    // No try/catch here: `AdminCrudFormModal` already awaits this function
    // and binds a rejected save's field errors via react-hook-form's
    // `setError`, plus a summary banner for anything unbound (non-field, or
    // a field this config doesn't render an input for) -- a toast here on
    // top would just duplicate the same message. Letting the rejection
    // propagate (rather than catching and re-throwing it) means
    // `handleCloseForm()` below is simply never reached on failure, and the
    // modal stays open with nothing extra to do.
    if (editingItem) {
      await updateMutation.mutateAsync({ id: editingItem.id, data: formData });
      toast.success(`${entity} updated successfully`);
    } else {
      await createMutation.mutateAsync(formData);
      toast.success(`${entity} created successfully`);
    }
    handleCloseForm();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success(`${entity} deleted successfully`);
      setDeleteTarget(null);
      // Remove from selection if selected
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(deleteTarget.id);
        return next;
      });
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleBulkDelete = async () => {
    try {
      const result = await bulkDeleteMutation.mutateAsync(Array.from(selectedIds));
      // Only what really went. `settleBulk` reports the refusals; claiming the
      // requested count here would say "12 items deleted" when 3 were refused.
      if (result.succeeded.length) {
        toast.success(
          `${result.succeeded.length} item${result.succeeded.length !== 1 ? 's' : ''} deleted`
        );
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleExport = useCallback(() => {
    // Build CSV from currently loaded items
    const headers = columns.map((c) => c.label).join(',');
    const rows = items.map((item) =>
      columns
        .map((col) => {
          const value = (item as Record<string, unknown>)[col.key];
          // Escape commas and quotes in CSV values
          const str = String(value ?? '');
          return str.includes(',') || str.includes('"') ? `"${str.replace(/"/g, '""')}"` : str;
        })
        .join(',')
    );
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${queryKey}-export.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  }, [items, columns, queryKey]);

  const handleActivate = async () => {
    try {
      const result = await activateMutation.mutateAsync({ ids: Array.from(selectedIds), active: true });
      if (result.succeeded.length) toast.success(`${result.succeeded.length} activated`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleDeactivate = async () => {
    try {
      const result = await activateMutation.mutateAsync({ ids: Array.from(selectedIds), active: false });
      if (result.succeeded.length) toast.success(`${result.succeeded.length} deactivated`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // Selection helpers
  const isAllSelected = items.length > 0 && items.every((item) => selectedIds.has(item.id));

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((item) => item.id)));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Sort handler
  const handleSort = (key: string) => {
    setOrdering((prev) => {
      if (prev === key) return `-${key}`;
      if (prev === `-${key}`) return '';
      return key;
    });
    setPage(1);
  };

  const getSortIndicator = (key: string) => {
    if (ordering === key) return ' \u2191';
    if (ordering === `-${key}`) return ' \u2193';
    return '';
  };

  // Reset page on search/filter change
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
    setSelectedIds(new Set());
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
    setPage(1);
    setSelectedIds(new Set());
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{title}</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {totalCount} {totalCount === 1 ? 'record' : 'records'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasImportExport && (
            <ImportExportControls apiEndpoint={apiEndpoint} invalidateKeys={[queryKey]} />
          )}
          <Button
            variant="primary"
            leftIcon={<PlusIcon className="w-4 h-4" />}
            onClick={handleOpenCreate}
          >
            Add {entity}
          </Button>
        </div>
      </div>

      {/* Bulk Actions */}
      {/* The slot is always rendered at the bar's height so ticking a row does
          not push the table down (and unticking does not pull it back up). */}
      <div data-testid="bulk-action-slot" className="min-h-[3.5rem]">
        {selectedIds.size > 0 && (
          <BulkActionBar
            selectedCount={selectedIds.size}
            onDelete={handleBulkDelete}
            onExport={handleExport}
            onActivate={hasActivate ? handleActivate : undefined}
            onDeactivate={hasActivate ? handleDeactivate : undefined}
          />
        )}
      </div>

      {/* Filters */}
      <Card padding="md">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className={filters.length > 0 ? 'sm:col-span-2' : 'sm:col-span-4'}>
            <Input
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
            />
          </div>
          {filters.map((filter) => (
            <Select
              key={filter.key}
              options={[{ value: '', label: filter.label }, ...filter.options]}
              value={filterValues[filter.key] || ''}
              onChange={(e) => handleFilterChange(filter.key, e.target.value)}
            />
          ))}
        </div>
      </Card>

      {/* Table */}
      <Card padding="none">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <TableCellsIcon className="w-12 h-12 mx-auto text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
              No {title.toLowerCase()} found
            </h3>
            <p className="mt-2 text-gray-500 dark:text-gray-400">
              Get started by adding your first {entity.toLowerCase()}.
            </p>
            <Button
              variant="primary"
              className="mt-4"
              leftIcon={<PlusIcon className="w-4 h-4" />}
              onClick={handleOpenCreate}
            >
              Add {entity}
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table
              className={cn(
                'min-w-full divide-y divide-gray-200 dark:divide-gray-700',
                isFixedLayout && 'table-fixed'
              )}
            >
              {isFixedLayout && (
                <colgroup>
                  {/* Checkbox column, then one per data column, then Actions. */}
                  <col style={{ width: '2.5rem' }} />
                  {columns.map((col) => (
                    <col key={col.key} style={col.width ? { width: col.width } : undefined} />
                  ))}
                  <col />
                </colgroup>
              )}
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-6 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </th>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      title={isFixedLayout && truncateCells ? col.label : undefined}
                      className={cn(
                        'px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider',
                        col.sortable && 'cursor-pointer select-none hover:text-gray-900 dark:hover:text-white',
                        isFixedLayout && truncateCells && 'truncate'
                      )}
                      onClick={col.sortable ? () => handleSort(col.key) : undefined}
                    >
                      {col.label}
                      {col.sortable && getSortIndicator(col.key)}
                    </th>
                  ))}
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {items.map((item) => (
                  <tr
                    key={item.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                      selectedIds.has(item.id) ? 'bg-primary-50 dark:bg-primary-900/10' : ''
                    }`}
                  >
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(item.id)}
                        onChange={() => toggleSelect(item.id)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    {columns.map((col) => {
                      const content = col.render
                        ? col.render((item as Record<string, unknown>)[col.key], item)
                        : String((item as Record<string, unknown>)[col.key] ?? '');
                      // A title/tooltip only makes sense for content renderable as
                      // text -- a custom `render()` can return arbitrary JSX (the
                      // Status toggle switch on Insurance Types, for one), and
                      // stringifying that would show "[object Object]" on hover.
                      const isPlainText = typeof content === 'string' || typeof content === 'number';
                      return (
                        <td
                          key={col.key}
                          title={isFixedLayout && truncateCells && isPlainText ? String(content) : undefined}
                          className={cn(
                            'px-6 py-4 text-sm text-gray-700 dark:text-gray-300',
                            isFixedLayout && truncateCells ? 'truncate' : 'whitespace-nowrap'
                          )}
                        >
                          {content}
                        </td>
                      );
                    })}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        {renderView && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setViewTarget(item)}
                            title="View"
                            aria-label={`View ${(item as { name?: string }).name ?? entity}`}
                          >
                            <EyeIcon className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(item)} title="Edit">
                          <PencilIcon className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteTarget(item)}
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Page {page} of {totalPages} ({totalCount} results)
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      {isCustomCreateOpen &&
        renderCreateForm!({ onClose: handleCloseForm, onCreated: handleCustomCreated })}
      <AdminCrudFormModal
        isOpen={isFormOpen && !isCustomCreateOpen}
        onClose={handleCloseForm}
        formFields={formFields}
        initialData={editingItem as Record<string, unknown> | null}
        onSubmit={handleFormSubmit}
        title={editingItem ? `Edit ${entity}` : `Create ${entity}`}
      />

      {/* View Detail Modal */}
      {renderView && (
        <Modal
          isOpen={!!viewTarget}
          onClose={() => setViewTarget(null)}
          title={viewTarget ? (viewTitle ? viewTitle(viewTarget) : `${entity} details`) : ''}
          size="md"
        >
          {viewTarget && renderView(viewTarget)}
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={`Delete ${entity}`}
        size="sm"
      >
        <p className="text-gray-600 dark:text-gray-400">
          Are you sure you want to delete{' '}
          <strong>{(deleteTarget as { name?: string } | null)?.name || 'this item'}</strong>? This action cannot be
          undone.
        </p>
        {config.deleteWarning && (
          <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
            {config.deleteWarning}
          </div>
        )}
        <ModalFooter>
          <Button variant="outline" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} isLoading={deleteMutation.isPending}>
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
